import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Search, ShieldCheck, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { catalogKinds, catalogProviders, catalogSections, catalogTabs, type CatalogProvider } from "@/mocks/catalog";
import { goAnchor } from "./app-nav";
import { t, tn } from "@/lib/i18n";

/* Живой макет «Каталог каналов»: табы категорий, поиск и карточки провайдеров.
   Подключение имитируется в макете (авторизация, ключ, код виджета) — реальные аккаунты не затрагиваются. */

const TAB_ORDER = catalogTabs.map((tab) => tab.id).filter((id) => id !== "all");

function CatalogDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [items, setItems] = useState<CatalogProvider[]>(() => catalogProviders.map((p) => ({ ...p })));
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<CatalogProvider | null>(null);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const onNav = (id: string) => {
    if (id === "channels") return;
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{name}» в демо не открывается", { name: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const connected = items.filter((p) => p.connected).length;
  const countIn = (id: string) => (id === "all" ? items.length : items.filter((p) => p.category === id).length);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((p) => {
      if (tab !== "all" && p.category !== tab) return false;
      if (!q) return true;
      return `${p.name} ${p.description} ${p.provider}`.toLowerCase().includes(q);
    });
  }, [items, tab, query]);

  const groups = useMemo(() => {
    const order = tab === "all" ? TAB_ORDER : [tab];
    return order
      .map((id) => ({ id, meta: catalogSections[id], list: filtered.filter((p) => p.category === id) }))
      .filter((g) => g.list.length > 0);
  }, [filtered, tab]);

  const open = (p: CatalogProvider) => {
    setDraft(p);
    setSecret("");
  };

  const confirm = () => {
    if (!draft) return;
    if (draft.kind === "token" && secret.trim().length < 6) return;
    setBusy(true);
    const target = draft;
    setTimeout(() => {
      setItems((prev) => prev.map((p) => (p.id === target.id ? { ...p, connected: true } : p)));
      setBusy(false);
      setDraft(null);
      flash(t("{name}: канал подключён — диалоги уже идут во «Входящие»", { name: target.name }));
    }, 600);
  };

  const disconnect = (p: CatalogProvider) => {
    setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, connected: false } : x)));
    flash(t("{name}: канал отключён, история диалогов сохранена", { name: p.name }));
  };

  const canConfirm = Boolean(draft) && !busy && (draft?.kind !== "token" || secret.trim().length >= 6);

  return (
    <Band id="catalog" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → каналы → каталог каналов")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Подключено {n} из {total}", { n: connected, total: items.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "channels";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-3.5" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Ключи и авторизации видит только администратор. Продавцы получают диалоги, а не пароли сервисов.")}
              </p>
            </div>
          </div>

          {/* ── каталог ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-store-3-line text-base leading-none" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold leading-snug">{t("Каталог каналов")}</h3>
                  <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                    {t("Управляйте каналами и находите новые — чтобы отвечать клиентам там, где им удобно.")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Демо-режим: подробная справка по каталогу каналов в макете недоступна"))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            {/* табы категорий + поиск */}
            <div className="mt-4 flex flex-col gap-3 border-b border-border pb-0 md:flex-row md:items-end md:justify-between">
              <div className="flex gap-4 overflow-x-auto" role="tablist" aria-label={t("Категории каналов")}>
                {catalogTabs.map((tb) => {
                  const active = tab === tb.id;
                  return (
                    <button
                      key={tb.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setTab(tb.id)}
                      className={cn(
                        "relative -mb-px flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 pb-2.5 text-[12.5px] font-semibold transition",
                        active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {tb.label}
                      <span className={cn("rounded-full px-1.5 py-px text-[9.5px] tabular-nums", active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")}>
                        {countIn(tb.id)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <label className="relative block w-full min-w-0 md:mb-1.5 md:w-64">
                <span className="sr-only">{t("Поиск по каталогу каналов")}</span>
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("Поиск по каталогу каналов")}
                  className="h-9 w-full box-border rounded-btn border border-border bg-background ps-8 pe-3 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
              </label>
            </div>

            {/* группы карточек */}
            <div className="mt-4 flex flex-col gap-6">
              {groups.map((g) => (
                <div key={g.id}>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h4 className="flex items-center gap-1.5 text-[13px] font-semibold">
                      <i className={cn(g.meta?.icon, "text-[15px] leading-none text-primary")} aria-hidden />
                      {g.meta?.label}
                    </h4>
                    <p className="min-w-0 text-[11px] leading-relaxed text-muted-foreground">{g.meta?.hint}</p>
                  </div>

                  <div className="mt-2.5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {g.list.map((p) => (
                      <article
                        key={p.id}
                        className={cn(
                          "relative flex flex-col gap-3 rounded-card border bg-background/50 p-4 pt-4 transition",
                          p.connected ? "border-primary/45 bg-primary/5" : "border-border hover:border-primary/40 hover:bg-background",
                        )}
                      >
                        {p.popular ? (
                          <span className="absolute -top-2 end-3 inline-flex items-center gap-1 rounded-full border border-primary/25 bg-card px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
                            <i className="ri-thumb-up-line text-[10px] leading-none" aria-hidden />
                            {t("Популярный")}
                          </span>
                        ) : null}

                        <div className="flex items-start gap-2.5">
                          <div className="min-w-0 flex-1">
                            <h5 className="text-[13px] font-semibold leading-snug">{p.name}</h5>
                            <p className="mt-1 line-clamp-3 text-[11.5px] leading-relaxed text-muted-foreground">{p.description}</p>
                          </div>
                          <span
                            className="grid size-9 flex-none place-items-center rounded-full bg-primary/10 text-[17px] leading-none text-primary"
                            aria-hidden
                          >
                            <i className={p.icon} />
                          </span>
                        </div>

                        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border/60 pt-2.5">
                          <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                            {t("От")} <b className="font-semibold text-foreground/80">{p.provider}</b>
                          </span>
                          {p.connected ? (
                            <span className="flex flex-none items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                <Check className="size-2.5" strokeWidth={3} aria-hidden />
                                {t("Подключено")}
                              </span>
                              <button
                                type="button"
                                onClick={() => disconnect(p)}
                                aria-label={t("Отключить {name}", { name: p.name })}
                                className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-red-500/10 hover:text-red-500"
                              >
                                <i className="ri-link-unlink text-[13px] leading-none" aria-hidden />
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => open(p)}
                              className="flex-none cursor-pointer whitespace-nowrap rounded-btn border border-border px-3 py-1.5 text-[11px] font-semibold text-foreground transition hover:border-primary hover:bg-primary hover:text-primary-foreground"
                            >
                              {t("Подключить")}
                            </button>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}

              {groups.length === 0 ? (
                <div className="rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
                  <span className="mx-auto grid size-12 place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                    <i className="ri-search-eye-line text-xl leading-none" />
                  </span>
                  <p className="mt-3 text-[13px] font-semibold">{t("Ничего не нашлось")}</p>
                  <p className="mx-auto mt-1 max-w-sm text-[11.5px] leading-relaxed text-muted-foreground">
                    {t("Попробуйте другое название, провайдера или выберите «Все» — в каталоге {n} каналов.", { n: items.length })}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      setTab("all");
                    }}
                    className="mt-3 inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                  >
                    {t("Сбросить фильтры")}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
              <span>
                {tn(filtered.length, "канал", "канала", "каналов")} {t("в подборке · подключено {n} из {total}", { n: connected, total: items.length })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-add-circle-line text-[13px] leading-none text-primary" aria-hidden />
                {t("Своего провайдера можно подключить по API")}
              </span>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: подключение имитируется в макете и не затрагивает реальные аккаунты провайдеров.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно подключения ── */}
        {draft ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                confirm();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="catalog-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setDraft(null)}
                aria-label={t("Закрыть окно подключения")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className={draft.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{catalogKinds[draft.kind].label}</p>
                  <h3 id="catalog-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {t("Подключить {name}", { name: draft.name })}
                  </h3>
                  <p className="mt-1 text-[11px] text-muted-foreground">{t("Провайдер: {provider}", { provider: draft.provider })}</p>
                </div>
              </div>

              <p className="mt-3 text-[12px] leading-relaxed text-muted-foreground">{catalogKinds[draft.kind].hint}</p>

              <div className="mt-4 rounded-btn border border-border bg-background/60 p-3.5">
                {draft.kind === "token" ? (
                  <div className="grid gap-2">
                    <label htmlFor="catalog-secret" className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("API-ключ")}
                    </label>
                    <input
                      id="catalog-secret"
                      value={secret}
                      onChange={(e) => setSecret(e.target.value)}
                      placeholder={t("Например, key_live_9f2c47ab81de")}
                      autoComplete="off"
                      spellCheck={false}
                      className="h-10 w-full box-border rounded-btn border border-border bg-background px-3 font-mono text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {secret.trim().length >= 6 ? t("Ключ выглядит корректно — можно подключать.") : t("Ключ хранится в защищённом виде и доступен только администратору.")}
                    </p>
                  </div>
                ) : null}

                {draft.kind === "oauth" ? (
                  <p className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground">
                    <ShieldCheck className="mt-0.5 size-4 flex-none text-primary" aria-hidden />
                    {t("Откроем окно авторизации {provider}. Hub запросит только доступ к сообщениям и имени профиля — публиковать ничего не сможем.", { provider: draft.provider })}
                  </p>
                ) : null}

                {draft.kind === "code" ? (
                  <div className="grid gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Код виджета")}</span>
                    <code className="block overflow-x-auto rounded-btn bg-muted px-2.5 py-2 font-mono text-[11px] text-foreground">
                      &lt;script src=&quot;https://cdn.hub.example/widget.js&quot; data-hub=&quot;hub-7f31&quot; async&gt;&lt;/script&gt;
                    </code>
                    <p className="text-[11px] text-muted-foreground">{t("Вставьте код перед закрывающим тегом </body> на всех страницах сайта.")}</p>
                  </div>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={!canConfirm}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <i className="ri-plug-line" aria-hidden />}
                  {busy ? t("Подключаем…") : t("Подключить канал")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default CatalogDemo;